import {
  shortOrderRef,
  type MarketplaceFulfillmentStatus,
} from "@nexus/contracts";
import { Checkbox, Icon, cn } from "@nexus/ui";
import { useId, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { ClientMark } from "@/features/marketplace/partner/components/ClientMark";

import { formatOrderLineCount } from "../../lib/order-line-count-label";
import { resolveOrderType } from "../../lib/order-line-kind";
import { describeDeadline, localTodayYmd } from "../../lib/order-deadline";
import { HiddenByFilterNote } from "../../components/HiddenByFilterNote";
import {
  NO_STATUS_FILTER,
  opsTrackerLines,
  opsTrackerVideos,
} from "../../lib/status-filter-match";
import { describeOrderScript } from "../../lib/order-script-display";
import {
  describeDeadlineRollup,
  describeOrderAttention,
  describeStatusRollup,
  openOrderDeadlineYmd,
} from "../../lib/orders-list-model";
import { ORDERS_OVERDUE_ROW_CLASS } from "../../lib/orders-overdue-row";
import type { AdminOrderRow } from "../../types/marketplace.types";
import { CopyableId } from "../../components/orders-list/CopyableId";
import { ExtendDueMenu } from "../../components/orders-list/ExtendDueMenu";
import { OrderDeadlineRollup } from "../../components/orders-list/OrderDeadlineRollup";
import { OrderStatusRollup } from "../../components/orders-list/OrderStatusRollup";
import { OrderTypeBadge } from "../../components/orders-list/OrderTypeBadge";
import { VendorIdentity } from "../../components/orders-list/VendorIdentity";
import { formatMarketplaceSentColumn } from "../../lib/format-marketplace-timestamp";
import { ScriptReviewAction } from "../../components/ScriptReviewAction";
import { AdminOrderScriptRow } from "./AdminOrderScriptRow";
import {
  ADMIN_ORDERS_CELL_ACTIONS_CLASS,
  ADMIN_ORDERS_CELL_DUE_CLASS,
  ADMIN_ORDERS_CELL_PARTNER_CLASS,
  ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
  ADMIN_ORDERS_CELL_SENT_CLASS,
  ADMIN_ORDERS_CELL_STATUS_CLASS,
  ADMIN_ORDERS_CELL_TYPE_CLASS,
  ADMIN_ORDERS_GRID_CLASS,
  ADMIN_ORDERS_ROW_PADDING_CLASS,
} from "./admin-orders-grid";

interface AdminOrderTableRowProps {
  order: AdminOrderRow;
  rowIndex: number;
  selected: boolean;
  checked: boolean;
  selectionDisabled?: boolean;
  expanded: boolean;
  expandedScriptVideoIds: ReadonlySet<string>;
  /** Selected statuses: when non-empty, rows are trimmed server-side and matched rows highlighted. */
  statusFilter?: ReadonlySet<MarketplaceFulfillmentStatus>;
  /** The user opened the whole order from a hidden-rows note. */
  revealed?: boolean;
  /** Opens the whole order while a Status filter is on. */
  onReveal?: () => void;
  panelScriptId: string | null;
  panelVideoKey: string | null;
  canEditStatus: boolean;
  pendingScriptIds: ReadonlySet<string>;
  onCheckedChange: (checked: boolean) => void;
  onToggleExpanded: () => void;
  onToggleScriptVideosExpanded: (scriptId: string) => void;
  onOpenScript: (scriptId: string, videoKey?: string | null) => void;
  onScriptStatusChange: (
    scriptId: string,
    next: MarketplaceFulfillmentStatus,
  ) => void;
  onVideoStatusChange: (
    scriptId: string,
    videoKey: string,
    next: MarketplaceFulfillmentStatus,
  ) => void;
  /** Ops editors only; omit to hide the inline Extend on overdue rows. */
  onExtendDue?: (nextDueYmd: string) => void;
  extendPending?: boolean;
}

function formatDateOnly(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

export function AdminOrderTableRow({
  order,
  rowIndex,
  selected,
  checked,
  selectionDisabled = false,
  expanded,
  expandedScriptVideoIds,
  statusFilter = NO_STATUS_FILTER,
  revealed = false,
  onReveal,
  panelScriptId,
  panelVideoKey,
  canEditStatus,
  pendingScriptIds,
  onCheckedChange,
  onToggleExpanded,
  onToggleScriptVideosExpanded,
  onOpenScript,
  onScriptStatusChange,
  onVideoStatusChange,
  onExtendDue,
  extendPending = false,
}: AdminOrderTableRowProps) {
  const { t, i18n } = useTranslation();
  const panelId = useId();
  const todayYmd = localTodayYmd();
  const statusRollup = describeStatusRollup(order);
  const deadlineRollup = describeDeadlineRollup(order, todayYmd);
  const isOverdue = describeOrderAttention(order, todayYmd).overdue;
  const statusFilterActive = statusFilter.size > 0;
  const filterView = { active: statusFilterActive, revealed };
  const shownLines = opsTrackerLines(order, filterView);
  const hiddenLineCount = order.scripts.length - shownLines.length;
  const hasScripts = order.scripts.length > 0;
  const openDueYmd = openOrderDeadlineYmd(order);
  const overdueOpenDueYmd =
    describeDeadline(openDueYmd, todayYmd, order.vendor.workingWeekdays)
      ?.tone === "overdue"
      ? openDueYmd
      : null;
  const pendingReviewScript = order.scripts.find(
    (script) =>
      (script.fulfillmentStatus === "SUBMITTED_FOR_FEEDBACK" ||
        script.fulfillmentStatus === "SUBMITTED_FOR_FINAL_APPROVAL") &&
      Boolean(script.scriptReviewId || script.preflightUrl),
  );
  const pendingReviewTitle = pendingReviewScript
    ? describeOrderScript(
        pendingReviewScript,
        t("marketplace.orders.detail.unnamedScript", "Unnamed script"),
      ).title
    : null;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onToggleExpanded();
    } else if (event.key === "ArrowRight" && hasScripts && !expanded) {
      event.preventDefault();
      onToggleExpanded();
    } else if (event.key === "ArrowLeft" && hasScripts && expanded) {
      event.preventDefault();
      onToggleExpanded();
    }
  };

  let nextChildRowIndex = rowIndex + 1;

  return (
    <li role="presentation">
      <div
        role="row"
        aria-level={1}
        aria-rowindex={rowIndex}
        aria-selected={checked}
        aria-expanded={hasScripts ? expanded : undefined}
        aria-controls={hasScripts ? panelId : undefined}
        aria-label={t("marketplace.ordersDashboard.orderRowLabel", {
          client: order.tenant.name,
          partner: order.vendor.name,
          count: order.scripts.length,
        })}
        data-order-id={order.id}
        data-overdue={isOverdue ? "true" : undefined}
        data-tree-row
        tabIndex={0}
        className={cn(
          ADMIN_ORDERS_GRID_CLASS,
          ADMIN_ORDERS_ROW_PADDING_CLASS,
          "group/row min-h-14 cursor-pointer py-2 text-left outline-none transition-colors duration-150 ease-out hover:bg-muted/30 focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
          (selected || statusFilterActive) && "bg-primary-subtle/40",
          isOverdue && ORDERS_OVERDUE_ROW_CLASS,
        )}
        onClick={onToggleExpanded}
        onKeyDown={handleKeyDown}
      >
        <span role="gridcell" className="flex items-center">
          <Checkbox
            checked={checked}
            disabled={selectionDisabled}
            aria-label={t("marketplace.ordersDashboard.selectOrder", {
              client: order.tenant.name,
              defaultValue: "Select order from {{client}}",
            })}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            onCheckedChange={(next) => onCheckedChange(next === true)}
          />
        </span>

        <span role="gridcell" className="flex items-center">
          {hasScripts ? (
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={t(
                expanded
                  ? "marketplace.orders.list.collapseOrder"
                  : "marketplace.orders.list.expandOrder",
                { vendor: order.tenant.name },
              )}
              className="flex size-7 -ml-1.5 cursor-pointer items-center justify-center rounded-sm text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpanded();
              }}
            >
              <Icon
                name="chevron-right"
                size="sm"
                color="inherit"
                className={cn(
                  "transition-transform duration-200 ease-out motion-reduce:transition-none",
                  expanded && "rotate-90",
                )}
                aria-hidden
              />
            </button>
          ) : null}
        </span>

        <div role="gridcell" className="flex min-w-0 items-center gap-2.5">
          <ClientMark
            name={order.tenant.name}
            logoUrl={order.tenant.logoUrl}
            brandColor={order.tenant.brandColor}
            size="sm"
          />
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">
              {order.tenant.name}
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-caption text-muted-foreground">
              <CopyableId
                value={order.id}
                displayValue={shortOrderRef(order.id) ?? order.id}
                density="quiet"
                copyLabel={t("marketplace.orders.copyOrderId")}
                copiedMessage={t("marketplace.orders.copyOrderIdSuccess")}
                className="min-w-0"
              />
              <span aria-hidden className="shrink-0 text-muted-foreground/40">
                ·
              </span>
              <time
                dateTime={order.createdAt}
                className="hidden shrink-0 tabular-nums sm:inline"
              >
                {formatDateOnly(order.createdAt, i18n.language)}
              </time>
              <span className="truncate sm:hidden">{order.vendor.name}</span>
            </span>
          </span>
        </div>

        <div
          role="gridcell"
          className={cn(
            ADMIN_ORDERS_CELL_PARTNER_CLASS,
            "min-w-0 items-center gap-2",
          )}
        >
          <VendorIdentity
            name={order.vendor.name}
            logoUrl={order.vendor.logoUrl}
            className="size-6"
          />
          <span className="truncate text-xs text-foreground">
            {order.vendor.name}
          </span>
        </div>

        <span role="gridcell" className={ADMIN_ORDERS_CELL_TYPE_CLASS}>
          <OrderTypeBadge type={resolveOrderType(order.scripts)} />
        </span>

        <span
          role="gridcell"
          className={cn(
            ADMIN_ORDERS_CELL_SCRIPTS_CLASS,
            "text-xs tabular-nums text-muted-foreground",
          )}
        >
          {statusFilterActive && !revealed
            ? t("marketplace.statusFilter.scriptsOfTotal", "{{visible}} of {{total}}", {
                visible: shownLines.length,
                total: order.scripts.length,
              })
            : formatOrderLineCount(order.scripts, t)}
        </span>

        <span role="gridcell" className={ADMIN_ORDERS_CELL_STATUS_CLASS}>
          <OrderStatusRollup rollup={statusRollup} />
        </span>

        <time
          role="gridcell"
          dateTime={order.sentAt ?? order.createdAt}
          className={cn(
            ADMIN_ORDERS_CELL_SENT_CLASS,
            "text-xs tabular-nums text-muted-foreground",
          )}
        >
          {formatMarketplaceSentColumn(
            order.sentAt ?? order.createdAt,
            i18n.language,
          )}
        </time>

        <span role="gridcell" className={ADMIN_ORDERS_CELL_DUE_CLASS}>
          <OrderDeadlineRollup
            rollup={deadlineRollup}
            todayYmd={todayYmd}
            workingWeekdays={order.vendor.workingWeekdays}
            createdAt={order.createdAt}
          />
        </span>

        <span role="gridcell" className={ADMIN_ORDERS_CELL_ACTIONS_CLASS}>
          {pendingReviewScript && pendingReviewTitle ? (
            <ScriptReviewAction
              scriptReviewId={pendingReviewScript.scriptReviewId}
              preflightUrl={pendingReviewScript.preflightUrl}
              label={t("marketplace.orders.list.review", "Review")}
              ariaLabel={t("marketplace.orders.list.reviewVideoAria", {
                title: pendingReviewTitle,
                defaultValue: "Review video for {{title}} (opens in a new tab)",
              })}
              variant="default"
              size="sm"
              compact
            />
          ) : onExtendDue && overdueOpenDueYmd ? (
            <ExtendDueMenu
              dueYmd={overdueOpenDueYmd}
              todayYmd={todayYmd}
              pending={extendPending}
              size="xs"
              onExtend={onExtendDue}
            />
          ) : null}
        </span>
      </div>

      {expanded && hasScripts ? (
        <ul
          id={panelId}
          role="presentation"
          className="flex flex-col divide-y divide-border-subtle/70 border-t border-border-subtle/70"
        >
          {shownLines.map((script) => {
            const index = order.scripts.indexOf(script);
            const scriptRowIndex = nextChildRowIndex;
            const videosExpanded = expandedScriptVideoIds.has(script.id);
            const videoRows =
              videosExpanded ? opsTrackerVideos(script, filterView).length : 0;
            nextChildRowIndex += 1 + videoRows;

            return (
              <AdminOrderScriptRow
                key={script.id}
                order={order}
                script={script}
                index={index + 1}
                rowIndex={scriptRowIndex}
                highlighted={panelScriptId === script.id}
                statusFilter={statusFilter}
                revealed={revealed}
                onReveal={onReveal}
                selectedVideoKey={
                  panelScriptId === script.id ? panelVideoKey : null
                }
                videosExpanded={videosExpanded}
                onToggleVideosExpanded={() =>
                  onToggleScriptVideosExpanded(script.id)
                }
                canEditStatus={canEditStatus}
                pending={pendingScriptIds.has(script.id)}
                uniformDeadline={deadlineRollup.isUniform}
                onOpen={() => onOpenScript(script.id, null)}
                onStatusChange={(next) => onScriptStatusChange(script.id, next)}
                onVideoStatusChange={(videoKey, next) =>
                  onVideoStatusChange(script.id, videoKey, next)
                }
              />
            );
          })}
          {hiddenLineCount > 0 ? (
            <li role="presentation">
              <HiddenByFilterNote
                kind="lines"
                count={hiddenLineCount}
                onReveal={onReveal}
              />
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}
